import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '@/components/ui/dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, FileText, Download, Printer, CalendarClock, DollarSign, ListChecks, ShieldCheck, Tag, Receipt, Search, Trash2, BadgeCheck } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import InventorySearchPicker from './InventorySearchPicker';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const downloadOrOpenPdf = (dataUrl, filename, action) => {
  const base64 = dataUrl.split(',')[1];
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  const blob = new Blob([bytes], { type: 'application/pdf' });
  const url = URL.createObjectURL(blob);
  if (action === 'download') {
    const a = document.createElement('a');
    a.href = url; a.download = filename;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
  } else if (action === 'print') {
    const w = window.open(url, '_blank');
    if (w) w.onload = () => w.print();
  } else {
    window.open(url, '_blank');
  }
  setTimeout(() => URL.revokeObjectURL(url), 60000);
};

export default function InstallmentManagement() {
  const { user } = useAuth();
  const canEditDelete = user?.role === 'Super Admin' || user?.role === 'Admin';
  const handleDeleteAgreement = async (ins) => {
    if (!window.confirm(`Delete agreement ${ins.agreement_number}? This removes the schedule and every payment record.`)) return;
    try {
      await axios.delete(`${API_URL}/api/installments/${ins.id}`, { withCredentials: true });
      toast.success('Agreement deleted');
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
  };
  const [installments, setInstallments] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [allInventory, setAllInventory] = useState([]);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [generating, setGenerating] = useState(null);

  // Payment dialog
  const [paymentDialog, setPaymentDialog] = useState({ open: false, installment: null });
  const [paymentForm, setPaymentForm] = useState({
    amount: '', payment_method: 'Cash', notes: '',
    installment_no: '', due_date: '', paid_date: '',
    late_fee: 0, tid: '', remarks: ''
  });
  // Search across the Active Installments table (client-side, live)
  const [tableSearch, setTableSearch] = useState('');
  // Customer picker search inside Create Installment dialog
  const [customerPickerQuery, setCustomerPickerQuery] = useState('');
  const [paymentSubmitting, setPaymentSubmitting] = useState(false);

  // Schedule sheet
  const [scheduleSheet, setScheduleSheet] = useState({ open: false, installment: null, data: null, payments: [], loading: false });

  // Surety cheque dialog
  const [suretyDialog, setSuretyDialog] = useState({ open: false, installment: null });
  const [suretyForm, setSuretyForm] = useState({ account_title: '', bank_name: '', bank_branch: '', bank_account_number: '', cheque_numbers: '' });
  const [suretySubmitting, setSuretySubmitting] = useState(false);

  // Box Label JPG preview dialog
  const [labelPreview, setLabelPreview] = useState({ open: false, installment: null, image: null, loading: false });

  const todayIso = new Date().toISOString().slice(0, 10);
  const [formData, setFormData] = useState({
    customer_id: '', inventory_id: '', plan_id: '', down_payment: '', down_payment_touched: false,
    registration_date: todayIso, first_installment_date: todayIso,
    // Manual override at handover — bypasses Plan Master math for this
    // agreement only. Not saved to Plan Master.
    manual_mode: false,
    manual_total_amount: '',
    manual_advance_payment: '',
    manual_months: '',
  });
  // Live breakdown from /api/installment-plans/calculate/{plan_id}
  const [breakdown, setBreakdown] = useState(null);
  const [breakdownLoading, setBreakdownLoading] = useState(false);

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    try {
      setLoading(true);
      const [insRes, custRes, invRes, allInvRes, plansRes] = await Promise.all([
        axios.get(`${API_URL}/api/installments`, { withCredentials: true }),
        axios.get(`${API_URL}/api/customers`, { withCredentials: true }),
        axios.get(`${API_URL}/api/inventory?is_sold=false`, { withCredentials: true }),
        axios.get(`${API_URL}/api/inventory`, { withCredentials: true }),
        axios.get(`${API_URL}/api/installment-plans?active_only=true`, { withCredentials: true }),
      ]);
      setInstallments(insRes.data);
      setCustomers(custRes.data);
      setInventory(invRes.data);
      setAllInventory(allInvRes.data);
      setPlans(plansRes.data || []);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const selectedInventoryIds = (formData.inventory_ids && formData.inventory_ids.length)
    ? formData.inventory_ids
    : (formData.inventory_id ? [formData.inventory_id] : []);
  const selectedInventories = selectedInventoryIds
    .map((id) => (allInventory && allInventory.find((x) => x.id === id)) || inventory.find((x) => x.id === id))
    .filter(Boolean);
  const selectedInventory = selectedInventories[0];
  const selectedPlan = plans.find(p => p.id === formData.plan_id);
  // Multi-product base price = sum of all selected products' sale prices
  const salePrice = selectedInventories.reduce((n, i) => n + (i?.sale_price || 0), 0);

  // Recalculate breakdown whenever inventory / plan / down_payment changes.
  useEffect(() => {
    if (formData.manual_mode) { setBreakdown(null); return; }
    if (!formData.plan_id || !selectedInventory) { setBreakdown(null); return; }
    let cancelled = false;
    (async () => {
      try {
        setBreakdownLoading(true);
        const params = new URLSearchParams({ product_price: String(salePrice) });
        // Only pass down_payment once the user has actually edited it — else backend uses min_down.
        if (formData.down_payment_touched && formData.down_payment !== '') {
          params.set('down_payment', String(Number(formData.down_payment) || 0));
        }
        const { data } = await axios.get(
          `${API_URL}/api/installment-plans/calculate/${formData.plan_id}?${params.toString()}`,
          { withCredentials: true }
        );
        if (cancelled) return;
        setBreakdown(data);
        // Auto-fill down payment with plan default if user hasn't touched it yet
        if (!formData.down_payment_touched) {
          setFormData(f => ({ ...f, down_payment: String(Math.round(data.min_down_payment || 0)) }));
        }
      } catch (err) {
        if (!cancelled) toast.error(err.response?.data?.detail || 'Failed to compute plan');
      } finally { if (!cancelled) setBreakdownLoading(false); }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.manual_mode, formData.plan_id, formData.inventory_id, JSON.stringify(formData.inventory_ids || []), formData.down_payment, salePrice]);

  // Live math for Manual override mode
  const manualTotal = Number(formData.manual_total_amount) || 0;
  const manualAdvance = Number(formData.manual_advance_payment) || 0;
  const manualMonths = Number(formData.manual_months) || 0;
  const manualRemaining = Math.max(0, manualTotal - manualAdvance);
  const manualMonthly = manualMonths > 0 ? Math.round((manualRemaining / manualMonths) * 100) / 100 : 0;
  const manualError =
    manualTotal <= 0 ? 'Total Amount must be greater than 0' :
    manualMonths <= 0 ? 'Months must be greater than 0' :
    manualAdvance < 0 ? 'Down Payment cannot be negative' :
    manualAdvance > manualTotal ? 'Down Payment cannot exceed Total Amount' : '';

  const handleSubmit = async (e) => {
    e.preventDefault();
    const inventoryIds = formData.inventory_ids && formData.inventory_ids.length
      ? formData.inventory_ids
      : (formData.inventory_id ? [formData.inventory_id] : []);
    if (!formData.customer_id || inventoryIds.length === 0) { toast.error('Select customer and at least one product'); return; }
    if (!formData.registration_date) { toast.error('Registration Date is required'); return; }
    if (!formData.first_installment_date) { toast.error('1st Installment Date is required'); return; }

    if (formData.manual_mode) {
      if (manualError) { toast.error(manualError); return; }
    } else {
      if (!formData.plan_id) { toast.error('Select an installment plan'); return; }
      if (!breakdown) { toast.error('Waiting for plan calculation…'); return; }
    }

    try {
      const payload = {
        customer_id: formData.customer_id,
        inventory_id: inventoryIds[0], // legacy compat
        line_items: inventoryIds.map((id) => ({ inventory_id: id })),
        start_date: new Date(formData.first_installment_date).toISOString(),
        registration_date: new Date(formData.registration_date).toISOString(),
        first_installment_date: new Date(formData.first_installment_date).toISOString(),
      };
      if (formData.manual_mode) {
        payload.manual_mode = true;
        payload.manual_total_amount = manualTotal;
        payload.manual_advance_payment = manualAdvance;
        payload.manual_months = manualMonths;
        payload.plan_months = manualMonths;
        payload.down_payment = manualAdvance;
        payload.monthly_amount = manualMonthly;
      } else {
        payload.plan_id = formData.plan_id;
        payload.plan_months = breakdown.months;
        payload.down_payment = Number(formData.down_payment || 0);
      }
      await axios.post(`${API_URL}/api/installments`, payload, { withCredentials: true });
      toast.success(`Installment agreement created${inventoryIds.length > 1 ? ` with ${inventoryIds.length} products` : ''}${formData.manual_mode ? ' (Manual)' : ''}`);
      setDialogOpen(false);
      setFormData({ customer_id: '', inventory_id: '', inventory_ids: [], plan_id: '', down_payment: '', down_payment_touched: false, registration_date: todayIso, first_installment_date: todayIso, manual_mode: false, manual_total_amount: '', manual_advance_payment: '', manual_months: '' });
      setCustomerPickerQuery('');
      setBreakdown(null);
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create');
    }
  };

  const handleAgreement = async (installmentId, action) => {
    try {
      setGenerating(installmentId);
      const { data } = await axios.get(`${API_URL}/api/installments/agreement/${installmentId}`, { withCredentials: true });
      downloadOrOpenPdf(data.agreement_pdf, `Agreement_${data.agreement_number}.pdf`, action);
      toast.success(action === 'download' ? 'Downloaded' : action === 'print' ? 'Opening for print' : 'Opened');
      fetchAll();
    } catch { toast.error('Failed to generate agreement'); }
    finally { setGenerating(null); }
  };

  const openPaymentDialog = (installment, row = null) => {
    const isDp = !!row?.is_down_payment || row?.installment_no === 0;
    // Iter39 — DP amount defaults to the REMAINING DP (schedule row 0's
    // `remaining`) so partial-DP customers see the outstanding amount, not
    // the original full one. First-time click still uses installment.down_payment.
    const dpRemaining = isDp
      ? Number((row?.remaining ?? row?.amount_due ?? installment.down_payment) || 0)
      : 0;
    setPaymentForm({
      amount: isDp
        ? dpRemaining
        : (row ? (row.remaining || row.amount_due || installment.monthly_amount || 0) : (installment.monthly_amount || 0)),
      payment_method: 'Cash',
      notes: '',
      paid_date: new Date().toISOString().slice(0, 10),
      installment_no: row?.installment_no || '',
      due_date: row?.due_date || '',
      tid: '',
      remarks: '',
      is_dp: isDp,
      // Payment classifier — used for the on-dialog badge, receipt label
      // and future filtering. Uses the schedule row + entered amount.
      _row_status: row?.status || 'pending',            // 'pending' | 'overdue' | 'partial' | 'paid'
      _row_amount_due: Number(row?.amount_due || (isDp ? installment.down_payment : installment.monthly_amount) || 0),
      _row_already_paid: Number(row?.paid_amount || 0),
      _dp_total: isDp ? Number(installment.down_payment || 0) : 0,
      _dp_remaining: dpRemaining,
    });
    setPaymentDialog({ open: true, installment });
  };

  // Derive Advance / Partial / Regular / Paid Off from what the user typed.
  // Called in-render — no side effects.
  const classifyPayment = (form) => {
    if (form.is_dp) {
      // Iter39 — partial down payment support. Split into Advance / Partial /
      // Final based on how much of the DP has already been received.
      const entered = Number(form.amount || 0);
      const already = Number(form._row_already_paid || 0);
      const total = Number(form._dp_total || form._row_amount_due || 0);
      const combined = already + entered;
      if (combined + 0.01 < total) {
        return { label: already > 0 ? 'Down Payment (Partial)' : 'Down Payment (Advance)',
                 tone: 'bg-cyan-500/15 text-cyan-300 border-cyan-500/40' };
      }
      return { label: already > 0 ? 'Down Payment (Final)' : 'Down Payment',
               tone: 'bg-cyan-500/15 text-cyan-300 border-cyan-500/40' };
    }
    const monthly = Number(form._row_amount_due || 0);
    const entered = Number(form.amount || 0);
    const already = Number(form._row_already_paid || 0);
    const combined = already + entered;
    if (form._row_status === 'pending' && !form.due_date_passed) {
      // Row's due date hasn't hit yet AND status is pending → this is advance.
      if (combined + 0.01 < monthly) return { label: 'Advance · Partial', tone: 'bg-blue-500/15 text-blue-300 border-blue-500/40' };
      return { label: 'Advance Payment', tone: 'bg-blue-500/15 text-blue-300 border-blue-500/40' };
    }
    if (entered > 0 && combined + 0.01 < monthly) {
      return { label: 'Partial Payment', tone: 'bg-amber-500/15 text-amber-300 border-amber-500/40' };
    }
    if (form._row_status === 'partial' && combined + 0.01 >= monthly) {
      return { label: 'Final Settlement', tone: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/40' };
    }
    return { label: 'Regular Payment', tone: 'bg-slate-700 text-slate-200 border-slate-600' };
  };

  const handlePayment = async () => {
    if (!paymentForm.paid_date) { toast.error('Receiving Date is required'); return; }
    try {
      setPaymentSubmitting(true);
      const isDp = !!paymentForm.is_dp;
      const paidIso = new Date(paymentForm.paid_date).toISOString();
      const url = isDp
        ? `${API_URL}/api/installments/down-payment-receipt/${paymentDialog.installment.id}`
        : `${API_URL}/api/installments/payment/${paymentDialog.installment.id}`;
      const payload = isDp
        ? {
            // Iter39 — partial DP support: send the entered amount so the
            // backend can create a slice-sized payment record instead of
            // charging the full DP every time.
            amount: Number(paymentForm.amount || 0),
            paid_date: paidIso,
            payment_method: paymentForm.payment_method,
            transaction_id: paymentForm.tid || null,
            notes: paymentForm.notes || null,
            payment_type: classifyPayment(paymentForm).label,
          }
        : {
            amount: Number(paymentForm.amount),
            payment_method: paymentForm.payment_method,
            notes: paymentForm.notes || null,
            paid_date: paidIso,
          };
      // Regular payments carry installment_no + due_date so the schedule row
      // flips to "paid" and dashboard KPIs update immediately.
      if (!isDp && paymentForm.installment_no) payload.installment_no = Number(paymentForm.installment_no);
      if (!isDp && paymentForm.due_date) payload.due_date = new Date(paymentForm.due_date).toISOString();
      // Tag the payment kind (Advance / Partial / Regular) so the payment
      // history + receipt notes carry a clear classification.
      if (!isDp) {
        const cls = classifyPayment(paymentForm);
        payload.payment_type = cls.label;
      }

      const { data } = await axios.post(url, payload, { withCredentials: true });
      toast.success(isDp
        ? `Down Payment confirmed — Receipt ${data.receipt_number}`
        : `Payment recorded — Receipt ${data.receipt_number}`);
      // Auto-open thermal receipt
      downloadOrOpenPdf(data.receipt_pdf || data.pdf,
        `${isDp ? 'DownPaymentReceipt' : 'Receipt'}_${data.receipt_number}.pdf`, 'print');
      const paidInstallmentId = paymentDialog.installment.id;
      setPaymentDialog({ open: false, installment: null });
      fetchAll();
      if (scheduleSheet.open && scheduleSheet.installment?.id === paidInstallmentId) {
        const [scheduleRes, paymentsRes] = await Promise.all([
          axios.get(`${API_URL}/api/installments/schedule/${paidInstallmentId}`, { withCredentials: true }),
          axios.get(`${API_URL}/api/installments/payments/${paidInstallmentId}`, { withCredentials: true }),
        ]);
        setScheduleSheet(s => ({ ...s, data: scheduleRes.data, payments: paymentsRes.data || [] }));
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Payment failed');
    } finally {
      setPaymentSubmitting(false);
    }
  };

  const openSchedule = async (installment) => {
    setScheduleSheet({ open: true, installment, data: null, payments: [], loading: true });
    try {
      const [scheduleRes, paymentsRes] = await Promise.all([
        axios.get(`${API_URL}/api/installments/schedule/${installment.id}`, { withCredentials: true }),
        axios.get(`${API_URL}/api/installments/payments/${installment.id}`, { withCredentials: true }),
      ]);
      setScheduleSheet({ open: true, installment, data: scheduleRes.data, payments: paymentsRes.data || [], loading: false });
    } catch {
      toast.error('Failed to load schedule');
      setScheduleSheet({ open: false, installment: null, data: null, payments: [], loading: false });
    }
  };

  const handleSchedulePdf = async (installment, action) => {
    try {
      const { data } = await axios.get(`${API_URL}/api/installments/schedule-pdf/${installment.id}`, { withCredentials: true });
      downloadOrOpenPdf(data.pdf, `Schedule_${data.agreement_number}.pdf`, action);
      toast.success(action === 'print' ? 'Printing schedule...' : 'Schedule downloaded');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate schedule PDF');
    }
  };

  const openSurety = (installment) => {
    const cust = customers.find(c => c.id === installment.customer_id);
    setSuretyForm({ account_title: cust?.name || '', bank_name: '', bank_branch: '', bank_account_number: '', cheque_numbers: '' });
    setSuretyDialog({ open: true, installment });
  };

  const handleSurety = async () => {
    try {
      setSuretySubmitting(true);
      const { data } = await axios.post(
        `${API_URL}/api/installments/surety-cheque/${suretyDialog.installment.id}`,
        suretyForm, { withCredentials: true }
      );
      downloadOrOpenPdf(data.pdf, `SuretyCheque_${data.agreement_number}.pdf`, 'download');
      toast.success('Surety cheque consent generated');
      setSuretyDialog({ open: false, installment: null });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate');
    } finally { setSuretySubmitting(false); }
  };

  const handleBoxLabel = async (installment) => {
    setLabelPreview({ open: true, installment, image: null, loading: true });
    try {
      const { data } = await axios.get(
        `${API_URL}/api/installments/box-label-jpg/${installment.id}`,
        { withCredentials: true }
      );
      setLabelPreview({ open: true, installment, image: data.image, loading: false });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate label');
      setLabelPreview({ open: false, installment: null, image: null, loading: false });
    }
  };

  // Down Payment shares the regular payment dialog — see openPaymentDialog +
  // handlePayment above. When the schedule row is is_down_payment the dialog
  // switches to DP mode and posts to /down-payment-receipt/{id}.

  const printLabelImage = () => {
    if (!labelPreview.image) return;
    const w = window.open('', '_blank', 'width=400,height=300');
    if (!w) return;
    w.document.write(`<html><head><title>Box Label</title>
      <style>body{margin:0;display:flex;align-items:center;justify-content:center;height:100vh;background:#fff;}
      img{max-width:100%;max-height:100vh;}
      @media print{ @page{ size: 2in 1in; margin:0; } body{ height:auto; } img{ width:2in; height:1in; object-fit:contain; } }
      </style></head><body><img src="${labelPreview.image}" onload="setTimeout(()=>{window.print();window.close();},200);" /></body></html>`);
    w.document.close();
  };

  const getCustomerName = (id) => customers.find(c => c.id === id)?.name || '—';
  // Iter40 — richer helpers for the extended agreement list / schedule sheet
  const getCustomer = (id) => customers.find(c => c.id === id) || null;
  const getInventory = (id) => allInventory.find(x => x.id === id) || null;
  const getInventoryLabel = (id) => {
    const i = getInventory(id);
    return i ? `${i.brand} ${i.model}` : '—';
  };
  const navigate = useNavigate();

  return (
    <div className="space-y-6" data-testid="installment-management">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2" data-testid="installments-title">Installments</h1>
          <p className="text-slate-400">Create plans, record payments, generate agreements</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-amber-500 hover:bg-amber-600 text-white" data-testid="add-installment-button">
              <Plus className="h-4 w-4 mr-2" /> New Installment
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-slate-900 border-slate-800 text-white max-w-2xl max-h-[90vh] overflow-y-auto"
            data-testid="create-installment-dialog">
            <DialogHeader>
              <DialogTitle className="text-white">Create Installment Plan</DialogTitle>
              <DialogDescription className="text-slate-400">Link customer with inventory and set plan.</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 pb-2">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2 col-span-2">
                  <Label className="text-slate-300">Customer * <span className="text-xs text-slate-500 ml-2">(search by name, account #, CNIC, agreement #, phone)</span></Label>
                  <Input
                    data-testid="customer-picker-search"
                    placeholder="Type to filter customers…"
                    value={customerPickerQuery}
                    onChange={(e) => setCustomerPickerQuery(e.target.value)}
                    className="bg-slate-800 border-slate-700 text-white"
                  />
                  <Select value={formData.customer_id} onValueChange={(v) => setFormData({ ...formData, customer_id: v })}>
                    <SelectTrigger className="bg-slate-800 border-slate-700 text-white" data-testid="select-customer"><SelectValue placeholder="Select customer" /></SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-700 text-white max-h-60">
                      {(() => {
                        const q = customerPickerQuery.trim().toLowerCase();
                        const filtered = q ? customers.filter(c => (
                          (c.name || '').toLowerCase().includes(q) ||
                          (c.account_number || '').toLowerCase().includes(q) ||
                          (c.cnic || '').toLowerCase().includes(q) ||
                          (c.mobile || '').toLowerCase().includes(q) ||
                          (c.customer_id || '').toLowerCase().includes(q)
                        )) : customers;
                        if (!filtered.length) return <div className="px-3 py-2 text-xs text-slate-500">No matching customers</div>;
                        return filtered.slice(0, 100).map((c) => (
                          <SelectItem key={c.id} value={c.id}>{c.name} — {c.account_number} · {c.cnic}</SelectItem>
                        ));
                      })()}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Registration Date *</Label>
                  <Input type="date" data-testid="registration-date-input"
                    value={formData.registration_date}
                    onChange={(e) => setFormData({ ...formData, registration_date: e.target.value })}
                    className="bg-slate-800 border-slate-700 text-white" />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">1st Installment Date *</Label>
                  <Input type="date" data-testid="first-installment-date-input"
                    value={formData.first_installment_date}
                    onChange={(e) => setFormData({ ...formData, first_installment_date: e.target.value })}
                    className="bg-slate-800 border-slate-700 text-white" />
                </div>
                <div className="space-y-2 col-span-2">
                  <Label className="text-slate-300">Products *</Label>
                  <p className="text-slate-500 text-xs">Search & add one or more products (Mobile + Laptop + Appliance in the same agreement is supported).</p>
                  <InventorySearchPicker
                    multi
                    selectedIds={formData.inventory_ids || (formData.inventory_id ? [formData.inventory_id] : [])}
                    onSelect={(item) => {
                      const currentIds = formData.inventory_ids || (formData.inventory_id ? [formData.inventory_id] : []);
                      if (currentIds.includes(item.id)) return;
                      const nextIds = [...currentIds, item.id];
                      setFormData({
                        ...formData,
                        inventory_ids: nextIds,
                        inventory_id: nextIds[0] || '',
                      });
                      setAllInventory((prev) => prev.some((x) => x.id === item.id) ? prev : [...prev, item]);
                      setInventory((prev) => prev.some((x) => x.id === item.id) ? prev : [...prev, item]);
                    }}
                    onRemove={(id) => {
                      const currentIds = formData.inventory_ids || (formData.inventory_id ? [formData.inventory_id] : []);
                      const nextIds = currentIds.filter((x) => x !== id);
                      setFormData({
                        ...formData,
                        inventory_ids: nextIds,
                        inventory_id: nextIds[0] || '',
                      });
                    }}
                  />
                </div>

                {/* Plan Mode Toggle: Plan Master vs Manual Handover */}
                <div className="col-span-2 flex gap-2 border border-slate-700 rounded-sm p-1 bg-slate-800">
                  <button type="button"
                    data-testid="mode-plan-master"
                    onClick={() => setFormData({ ...formData, manual_mode: false })}
                    className={`flex-1 py-2 rounded-sm text-sm font-medium transition ${
                      !formData.manual_mode ? 'bg-amber-500 text-slate-950' : 'text-slate-400 hover:text-white'
                    }`}>
                    Use Plan Master
                  </button>
                  <button type="button"
                    data-testid="mode-manual"
                    onClick={() => setFormData({ ...formData, manual_mode: true })}
                    className={`flex-1 py-2 rounded-sm text-sm font-medium transition ${
                      formData.manual_mode ? 'bg-amber-500 text-slate-950' : 'text-slate-400 hover:text-white'
                    }`}>
                    Manual Create Installment Plan
                  </button>
                </div>

                {formData.manual_mode ? (
                  <>
                    <div className="space-y-2">
                      <Label className="text-slate-300">Total Amount (PKR) *</Label>
                      <Input type="number" required
                        data-testid="manual-total-input"
                        value={formData.manual_total_amount}
                        onChange={(e) => setFormData({ ...formData, manual_total_amount: e.target.value })}
                        className="bg-slate-800 border-slate-700 text-white" />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-slate-300">Down Payment (PKR)</Label>
                      <Input type="number"
                        data-testid="manual-advance-input"
                        value={formData.manual_advance_payment}
                        onChange={(e) => setFormData({ ...formData, manual_advance_payment: e.target.value })}
                        className="bg-slate-800 border-slate-700 text-white" />
                    </div>
                    <div className="space-y-2 col-span-2">
                      <Label className="text-slate-300">Months *</Label>
                      <Input type="number" required
                        data-testid="manual-months-input"
                        value={formData.manual_months}
                        onChange={(e) => setFormData({ ...formData, manual_months: e.target.value })}
                        className="bg-slate-800 border-slate-700 text-white" />
                    </div>
                  </>
                ) : (
                  <>
                    <div className="space-y-2">
                      <Label className="text-slate-300">Plan *</Label>
                      <Select value={formData.plan_id} onValueChange={(v) => setFormData({ ...formData, plan_id: v, down_payment_touched: false })}>
                        <SelectTrigger className="bg-slate-800 border-slate-700 text-white" data-testid="select-plan"><SelectValue placeholder={plans.length ? 'Select plan' : 'No plans configured'} /></SelectTrigger>
                        <SelectContent className="bg-slate-800 border-slate-700 text-white max-h-60">
                          {plans.map((p) => (
                            <SelectItem key={p.id} value={p.id}>
                              {p.name} — {p.months}mo · {p.business_charge_percent}% BC · Min DP {p.min_down_payment_percent}%
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {plans.length === 0 && (
                        <p className="text-xs text-amber-500">No plans configured. Go to <b>Plan Master</b> and seed / add plans first.</p>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label className="text-slate-300">
                        Down Payment (PKR){selectedPlan && <span className="text-xs text-slate-400 ml-2">min {selectedPlan.min_down_payment_percent}% = PKR {Math.round((salePrice * selectedPlan.min_down_payment_percent) / 100).toLocaleString()}</span>}
                      </Label>
                      <Input type="number" data-testid="down-payment-input" value={formData.down_payment}
                        onChange={(e) => setFormData({ ...formData, down_payment: e.target.value, down_payment_touched: true })}
                        className="bg-slate-800 border-slate-700 text-white" />
                    </div>
                  </>
                )}
              </div>
              {formData.manual_mode ? (
                <div className="bg-slate-800/50 border border-amber-500/40 rounded-sm p-4 space-y-1 text-sm" data-testid="manual-breakdown">
                  <div className="text-slate-300 font-semibold mb-2">Manual Handover Plan — {manualMonths || 0} Months</div>
                  <div className="flex justify-between"><span className="text-slate-400">Total Amount:</span><span className="text-white">PKR {manualTotal.toLocaleString()}</span></div>
                  <div className="flex justify-between"><span className="text-slate-400">− Down Payment:</span><span className="text-amber-500">PKR {manualAdvance.toLocaleString()}</span></div>
                  <div className="flex justify-between border-t border-slate-700 pt-2 mt-2"><span className="text-slate-300 font-medium">Remaining Amount:</span><span className="text-emerald-500 font-bold" data-testid="manual-remaining-display">PKR {manualRemaining.toLocaleString()}</span></div>
                  <div className="flex justify-between border-t border-slate-700 pt-2 mt-2"><span className="text-slate-300 font-medium">Monthly Installment × {manualMonths || 0}:</span><span className="text-amber-500 font-bold text-lg" data-testid="manual-monthly-display">PKR {manualMonthly.toLocaleString()}</span></div>
                  {manualError && <div className="mt-2 text-red-400 text-xs" data-testid="manual-error">{manualError}</div>}
                </div>
              ) : selectedInventory && breakdown && (
                <div className="bg-slate-800/50 border border-slate-700 rounded-sm p-4 space-y-1 text-sm" data-testid="plan-breakdown">
                  <div className="text-slate-300 font-semibold mb-2">{breakdown.plan_name} — {breakdown.months} Months</div>
                  <div className="flex justify-between"><span className="text-slate-400">Sale Price (base):</span><span className="text-white">PKR {breakdown.product_price?.toLocaleString()}</span></div>
                  <div className="flex justify-between"><span className="text-slate-400">Business Charge ({breakdown.business_charge_percent}%):</span><span className="text-white">PKR {breakdown.business_charge?.toLocaleString()}</span></div>
                  <div className="flex justify-between"><span className="text-slate-400">Processing Fee:</span><span className="text-white">PKR {breakdown.processing_fee?.toLocaleString()}</span></div>
                  <div className="flex justify-between"><span className="text-slate-400">Document Charges:</span><span className="text-white">PKR {breakdown.document_charges?.toLocaleString()}</span></div>
                  <div className="flex justify-between"><span className="text-slate-400">Registration Fee:</span><span className="text-white">PKR {breakdown.registration_fee?.toLocaleString()}</span></div>
                  <div className="flex justify-between"><span className="text-slate-400">Insurance Fee:</span><span className="text-white">PKR {breakdown.insurance_fee?.toLocaleString()}</span></div>
                  <div className="flex justify-between"><span className="text-slate-400">Other Charges:</span><span className="text-white">PKR {breakdown.other_charges?.toLocaleString()}</span></div>
                  <div className="flex justify-between"><span className="text-slate-400">Tax ({breakdown.tax_percent}%):</span><span className="text-white">PKR {breakdown.tax_amount?.toLocaleString()}</span></div>
                  <div className="flex justify-between border-t border-slate-700 pt-2 mt-2"><span className="text-slate-300 font-medium">Total Agreement Amount:</span><span className="text-emerald-500 font-bold" data-testid="breakdown-total">PKR {breakdown.total_agreement_amount?.toLocaleString()}</span></div>
                  <div className="flex justify-between"><span className="text-slate-400">− Down Payment:</span><span className="text-amber-500">PKR {breakdown.down_payment?.toLocaleString()}</span></div>
                  <div className="flex justify-between"><span className="text-slate-300">Financed Remaining:</span><span className="text-white">PKR {breakdown.remaining?.toLocaleString()}</span></div>
                  <div className="flex justify-between border-t border-slate-700 pt-2 mt-2"><span className="text-slate-300 font-medium">Monthly Installment × {breakdown.months}:</span><span className="text-amber-500 font-bold text-lg" data-testid="breakdown-monthly">PKR {breakdown.monthly_installment?.toLocaleString()}</span></div>
                  <div className="text-xs text-slate-500 pt-2 border-t border-slate-800 mt-2">
                    Late fee rule (applied only on overdue installments): PKR {breakdown.late_payment_charge?.toLocaleString()}
                  </div>
                </div>
              )}
              {!formData.manual_mode && selectedInventory && !breakdown && formData.plan_id && (
                <div className="bg-slate-800/50 border border-slate-700 rounded-sm p-4 text-sm text-slate-400" data-testid="breakdown-loading">
                  {breakdownLoading ? 'Calculating plan…' : 'Select customer, inventory and plan to see the breakdown.'}
                </div>
              )}
              <div className="sticky bottom-0 -mx-6 px-6 py-3 mt-2 bg-slate-900 border-t border-slate-800 flex justify-end space-x-2">
                <Button type="button" variant="ghost" onClick={() => setDialogOpen(false)} className="text-slate-300">Cancel</Button>
                <Button type="submit" data-testid="submit-installment-button" className="bg-amber-500 hover:bg-amber-600">Create Plan</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="bg-slate-900 border-slate-800">
        <CardHeader>
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center space-x-2 text-slate-300">
              <CalendarClock className="h-5 w-5 text-amber-500" />
              <h2 className="text-lg font-semibold text-white">Active Installments</h2>
            </div>
            <div className="flex items-center space-x-2 flex-1 max-w-md">
              <Search className="h-4 w-4 text-slate-500" />
              <Input
                data-testid="installments-search-input"
                placeholder="Search by Account #, CNIC, Mobile, Tracking #, IMEI, serial / model, customer name…"
                value={tableSearch}
                onChange={(e) => setTableSearch(e.target.value)}
                className="bg-slate-800 border-slate-700 text-white h-9"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? <div className="text-center py-8 text-slate-400">Loading...</div>
            : (() => {
              const q = tableSearch.trim().toLowerCase();
              const rows = q ? installments.filter((i) => {
                const c = getCustomer(i.customer_id) || {};
                const inv = getInventory(i.inventory_id) || {};
                return (
                  (c.name || '').toLowerCase().includes(q) ||
                  (c.account_number || '').toLowerCase().includes(q) ||
                  (c.cnic || '').toLowerCase().includes(q) ||
                  (c.mobile || '').toLowerCase().includes(q) ||
                  (c.tracking_number || '').toLowerCase().includes(q) ||
                  (inv.imei || '').toLowerCase().includes(q) ||
                  (inv.serial_number || '').toLowerCase().includes(q) ||
                  (inv.brand || '').toLowerCase().includes(q) ||
                  (inv.model || '').toLowerCase().includes(q) ||
                  (i.agreement_number || '').toLowerCase().includes(q)
                );
              }) : installments;
              if (rows.length === 0) return (
                <div className="text-center py-8 text-slate-400" data-testid="no-installments-message">
                  {installments.length === 0 ? 'No installment plans yet.' : 'No installments match your search.'}
                </div>
              );
              return (
              <Table>
                <TableHeader>
                  <TableRow className="border-slate-800">
                    <TableHead className="text-slate-400 w-10"></TableHead>
                    <TableHead className="text-slate-400">Agreement</TableHead>
                    <TableHead className="text-slate-400">Customer</TableHead>
                    <TableHead className="text-slate-400">Tracking #</TableHead>
                    <TableHead className="text-slate-400">Product</TableHead>
                    <TableHead className="text-slate-400">Sales Exec</TableHead>
                    <TableHead className="text-slate-400">Server</TableHead>
                    <TableHead className="text-slate-400">Plan</TableHead>
                    <TableHead className="text-slate-400">Monthly</TableHead>
                    <TableHead className="text-slate-400">Paid</TableHead>
                    <TableHead className="text-slate-400">Remaining</TableHead>
                    <TableHead className="text-slate-400">Status</TableHead>
                    <TableHead className="text-slate-400">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((i) => {
                    const c = getCustomer(i.customer_id) || {};
                    return (
                    <TableRow key={i.id} className="border-slate-800 hover:bg-slate-800/50" data-testid="installment-row">
                      <TableCell>
                        {c.photo_url ? (
                          <img src={c.photo_url} alt="" className="h-8 w-8 rounded-full object-cover border border-slate-700" />
                        ) : (
                          <div className="h-8 w-8 rounded-full bg-slate-800 border border-slate-700 flex items-center justify-center text-[10px] text-slate-400">
                            {(c.name || '—').charAt(0).toUpperCase()}
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="text-white font-medium text-xs">
                        <button
                          type="button"
                          className="text-cyan-300 hover:text-cyan-200 underline-offset-2 hover:underline"
                          onClick={() => c.id && navigate(`/customers/${c.id}`)}
                          data-testid={`open-customer-${i.id}`}
                          title="Open customer profile"
                        >{i.agreement_number || '—'}</button>
                      </TableCell>
                      <TableCell className="text-white">{c.name || getCustomerName(i.customer_id)}</TableCell>
                      <TableCell className="text-slate-300 text-xs">{c.tracking_number || '—'}</TableCell>
                      <TableCell className="text-slate-300">{getInventoryLabel(i.inventory_id)}</TableCell>
                      <TableCell className="text-slate-300 text-xs">{c.sales_executive_name || i.sales_executive_name || '—'}</TableCell>
                      <TableCell className="text-slate-300 text-xs">{c.server_name || '—'}</TableCell>
                      <TableCell className="text-slate-300">{i.plan_months} mo</TableCell>
                      <TableCell className="text-slate-300">PKR {i.monthly_amount?.toLocaleString()}</TableCell>
                      <TableCell className="text-emerald-500">PKR {i.paid_amount?.toLocaleString()}</TableCell>
                      <TableCell className="text-amber-500">PKR {i.remaining_amount?.toLocaleString()}</TableCell>
                      <TableCell>
                        <span className={`px-2 py-1 rounded-sm text-xs ${
                          i.status === 'active' ? 'bg-emerald-500/20 text-emerald-500' :
                          i.status === 'completed' ? 'bg-blue-500/20 text-blue-500' :
                          i.status === 'defaulted' ? 'bg-red-500/20 text-red-500' :
                          'bg-yellow-500/20 text-yellow-500'
                        }`}>{i.status}</span>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center space-x-1">
                          <Button size="sm" variant="ghost" onClick={() => openPaymentDialog(i)}
                            disabled={i.status === 'completed'}
                            data-testid={`pay-${i.id}`}
                            className="text-emerald-500 hover:text-emerald-400 hover:bg-slate-800 h-8 w-8 p-0" title="Record Payment">
                            <DollarSign className="h-4 w-4" />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => openSchedule(i)}
                            data-testid={`schedule-${i.id}`}
                            className="text-blue-400 hover:text-blue-300 hover:bg-slate-800 h-8 w-8 p-0" title="Schedule">
                            <ListChecks className="h-4 w-4" />
                          </Button>
                          <Button size="sm" variant="ghost" disabled={generating === i.id}
                            onClick={() => handleAgreement(i.id, 'view')}
                            className="text-slate-300 hover:text-white hover:bg-slate-800 h-8 w-8 p-0" title="View Agreement">
                            <FileText className="h-4 w-4" />
                          </Button>
                          <Button size="sm" variant="ghost" disabled={generating === i.id}
                            onClick={() => handleAgreement(i.id, 'download')}
                            className="text-slate-300 hover:text-white hover:bg-slate-800 h-8 w-8 p-0" title="Download">
                            <Download className="h-4 w-4" />
                          </Button>
                          <Button size="sm" variant="ghost" disabled={generating === i.id}
                            onClick={() => handleAgreement(i.id, 'print')}
                            className="text-amber-500 hover:text-amber-400 hover:bg-slate-800 h-8 w-8 p-0" title="Print">
                            <Printer className="h-4 w-4" />
                          </Button>
                          <Button size="sm" variant="ghost"
                            onClick={() => openSurety(i)}
                            data-testid={`surety-${i.id}`}
                            className="text-purple-400 hover:text-purple-300 hover:bg-slate-800 h-8 w-8 p-0" title="Surety Cheque Consent">
                            <ShieldCheck className="h-4 w-4" />
                          </Button>
                          <Button size="sm" variant="ghost"
                            onClick={() => handleBoxLabel(i)}
                            data-testid={`box-label-view-${i.id}`}
                            className="text-cyan-400 hover:text-cyan-300 hover:bg-slate-800 h-8 w-8 p-0" title="View Box Label (JPG)">
                            <Tag className="h-4 w-4" />
                          </Button>
                          {canEditDelete && (
                            <Button size="sm" variant="ghost"
                              onClick={() => handleDeleteAgreement(i)}
                              data-testid={`delete-agreement-${i.id}`}
                              className="text-red-500 hover:text-red-400 hover:bg-slate-800 h-8 w-8 p-0" title="Delete Agreement (Super Admin/Admin only)">
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
              );
            })()
          }
        </CardContent>
      </Card>

      {/* Payment Dialog */}
      <Dialog open={paymentDialog.open} onOpenChange={(o) => setPaymentDialog({ ...paymentDialog, open: o })}>
        <DialogContent className="bg-slate-900 border-slate-800 text-white max-w-lg" data-testid={paymentForm.is_dp ? 'dp-payment-dialog' : 'payment-dialog'}>
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <span>{paymentForm.is_dp ? 'Record Down Payment' : 'Record Payment'}</span>
              {(() => {
                const c = classifyPayment(paymentForm);
                return <span className={`text-xs uppercase tracking-wider px-2 py-0.5 rounded-full border ${c.tone}`} data-testid="payment-type-badge">{c.label}</span>;
              })()}
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              {paymentDialog.installment && (
                <>
                  {paymentForm.is_dp
                    ? <span className="text-cyan-400 font-semibold mr-2">DP · Down Payment · </span>
                    : (paymentForm.installment_no && (
                        <span className="text-emerald-500 font-semibold mr-2">Installment #{paymentForm.installment_no} · </span>
                      ))}
                  Customer: <span className="text-white">{getCustomerName(paymentDialog.installment.customer_id)}</span> | Remaining: <span className="text-amber-500">PKR {paymentDialog.installment.remaining_amount?.toLocaleString()}</span>
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-slate-300 flex items-center gap-2">
                <span>Amount (PKR) *</span>
                {paymentForm.is_dp && (
                  <span className="text-xs text-cyan-300/80 ml-2" data-testid="dp-remaining-hint">
                    Outstanding DP: PKR {Number(paymentForm._dp_remaining || 0).toLocaleString()} of PKR {Number(paymentForm._dp_total || 0).toLocaleString()}
                  </span>
                )}
              </Label>
              <Input type="number" data-testid="payment-amount-input"
                value={paymentForm.amount}
                onChange={(e) => setPaymentForm({ ...paymentForm, amount: e.target.value })}
                max={paymentForm.is_dp ? (paymentForm._dp_remaining || undefined) : undefined}
                className="bg-slate-800 border-slate-700 text-white text-2xl font-bold h-14" />
              {paymentForm.is_dp && paymentForm._dp_total > 0 && (
                <div className="flex gap-2 text-[11px]">
                  <button
                    type="button"
                    onClick={() => setPaymentForm(f => ({ ...f, amount: Math.round((paymentForm._dp_remaining || 0) / 2) }))}
                    className="px-2 py-1 rounded bg-slate-800 border border-slate-700 text-cyan-300 hover:bg-slate-700"
                    data-testid="dp-half-btn"
                  >Half</button>
                  <button
                    type="button"
                    onClick={() => setPaymentForm(f => ({ ...f, amount: paymentForm._dp_remaining || 0 }))}
                    className="px-2 py-1 rounded bg-slate-800 border border-slate-700 text-cyan-300 hover:bg-slate-700"
                    data-testid="dp-full-btn"
                  >Full remaining</button>
                </div>
              )}
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Receiving Date * <span className="text-xs text-slate-500 ml-2">(actual date payment was received)</span></Label>
              <Input type="date" data-testid="payment-paid-date-input"
                value={paymentForm.paid_date}
                onChange={(e) => setPaymentForm({ ...paymentForm, paid_date: e.target.value })}
                className="bg-slate-800 border-slate-700 text-white" />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Payment Method</Label>
              <Select value={paymentForm.payment_method} onValueChange={(v) => setPaymentForm({ ...paymentForm, payment_method: v })}>
                <SelectTrigger className="bg-slate-800 border-slate-700 text-white" data-testid="payment-method-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700 text-white">
                  <SelectItem value="Cash">Cash</SelectItem>
                  <SelectItem value="Bank Transfer">Bank Transfer</SelectItem>
                  <SelectItem value="EasyPaisa">EasyPaisa</SelectItem>
                  <SelectItem value="JazzCash">JazzCash</SelectItem>
                  <SelectItem value="Cheque">Cheque</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {paymentForm.is_dp && paymentForm.payment_method !== 'Cash' && (
              <div className="space-y-2">
                <Label className="text-slate-300">Transaction / Reference ID</Label>
                <Input data-testid="payment-tid-input"
                  placeholder="Optional — cheque number, JazzCash TID, etc."
                  value={paymentForm.tid || ''}
                  onChange={(e) => setPaymentForm({ ...paymentForm, tid: e.target.value })}
                  className="bg-slate-800 border-slate-700 text-white" />
              </div>
            )}
            <div className="space-y-2">
              <Label className="text-slate-300">Notes</Label>
              <Input value={paymentForm.notes} onChange={(e) => setPaymentForm({ ...paymentForm, notes: e.target.value })}
                className="bg-slate-800 border-slate-700 text-white" />
            </div>
            <div className="flex justify-end space-x-2">
              <Button variant="ghost" onClick={() => setPaymentDialog({ open: false, installment: null })} className="text-slate-300">Cancel</Button>
              <Button onClick={handlePayment} disabled={paymentSubmitting || !paymentForm.amount}
                data-testid="submit-payment-button"
                className={paymentForm.is_dp ? 'bg-cyan-600 hover:bg-cyan-700' : 'bg-emerald-600 hover:bg-emerald-700'}>
                <Printer className="h-4 w-4 mr-2" />
                {paymentSubmitting
                  ? 'Recording...'
                  : (paymentForm.is_dp ? 'Confirm Down Payment' : 'Record & Print Receipt')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Schedule Sheet */}
      <Sheet open={scheduleSheet.open} onOpenChange={(o) => setScheduleSheet({ ...scheduleSheet, open: o })}>
        <SheetContent className="bg-slate-900 border-slate-800 text-white w-full sm:max-w-2xl overflow-y-auto" data-testid="schedule-sheet">          <SheetHeader>
            <SheetTitle className="text-white">Installment Schedule</SheetTitle>
            <SheetDescription className="text-slate-400">
              {scheduleSheet.installment && (
                <>{getCustomerName(scheduleSheet.installment.customer_id)} — {getInventoryLabel(scheduleSheet.installment.inventory_id)}</>
              )}
            </SheetDescription>
          </SheetHeader>
          {scheduleSheet.loading ? <div className="text-center py-8 text-slate-400 mt-6">Loading schedule...</div>
            : scheduleSheet.data && (() => {
              const _ins = scheduleSheet.installment;
              const _cust = getCustomer(_ins?.customer_id) || {};
              const _inv = getInventory(_ins?.inventory_id) || {};
              return (
              <div className="mt-6 space-y-4">
                {/* Iter40 — richer header with photo + customer + product details */}
                <div className="flex items-start gap-3 bg-slate-800/40 border border-slate-700 rounded-sm p-3">
                  {_cust.photo_url ? (
                    <img src={_cust.photo_url} alt="" className="h-16 w-16 rounded object-cover border border-slate-700" />
                  ) : (
                    <div className="h-16 w-16 rounded bg-slate-800 border border-slate-700 flex items-center justify-center text-2xl text-slate-500">
                      {(_cust.name || '—').charAt(0).toUpperCase()}
                    </div>
                  )}
                  <div className="flex-1 grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                    <div><span className="text-slate-400">Customer:</span> <span className="text-white font-medium">{_cust.name || '—'}</span></div>
                    <div><span className="text-slate-400">Account #:</span> <span className="text-cyan-300">{_cust.account_number || '—'}</span></div>
                    <div><span className="text-slate-400">Tracking #:</span> <span className="text-cyan-300">{_cust.tracking_number || '—'}</span></div>
                    <div><span className="text-slate-400">Mobile:</span> <span className="text-slate-200">{_cust.mobile || '—'}</span></div>
                    <div><span className="text-slate-400">Product:</span> <span className="text-slate-200">{_inv.brand || ''} {_inv.model || ''}</span></div>
                    <div><span className="text-slate-400">IMEI:</span> <span className="text-slate-200">{_inv.imei || '—'}</span></div>
                    <div><span className="text-slate-400">Serial:</span> <span className="text-slate-200">{_inv.serial_number || '—'}</span></div>
                    <div><span className="text-slate-400">Agreement #:</span> <span className="text-white">{_ins?.agreement_number || '—'}</span></div>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-slate-800/50 border border-slate-700 rounded-sm p-3">
                    <div className="text-xs text-slate-400">Total</div>
                    <div className="text-lg font-semibold text-white">PKR {scheduleSheet.data.total_amount?.toLocaleString()}</div>
                  </div>
                  <div className="bg-slate-800/50 border border-slate-700 rounded-sm p-3">
                    <div className="text-xs text-slate-400">Paid</div>
                    <div className="text-lg font-semibold text-emerald-500">PKR {scheduleSheet.data.total_paid?.toLocaleString()}</div>
                  </div>
                  <div className="bg-slate-800/50 border border-slate-700 rounded-sm p-3">
                    <div className="text-xs text-slate-400">Remaining</div>
                    <div className="text-lg font-semibold text-amber-500">PKR {scheduleSheet.data.total_remaining?.toLocaleString()}</div>
                  </div>
                  <div className="bg-slate-800/50 border border-slate-700 rounded-sm p-3">
                    <div className="text-xs text-slate-400">Down Payment</div>
                    <div className="text-lg font-semibold text-white">PKR {scheduleSheet.data.down_payment?.toLocaleString()}</div>
                  </div>
                </div>

                <Table>
                  <TableHeader>
                    <TableRow className="border-slate-800">
                      <TableHead className="text-slate-400">#</TableHead>
                      <TableHead className="text-slate-400">Due Date</TableHead>
                      <TableHead className="text-slate-400">Amount</TableHead>
                      <TableHead className="text-slate-400">Paid</TableHead>
                      <TableHead className="text-slate-400">Receiving Date</TableHead>
                      <TableHead className="text-slate-400">Remaining</TableHead>
                      <TableHead className="text-slate-400">Status</TableHead>
                      <TableHead className="text-slate-400 text-right">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {scheduleSheet.data.schedule.map((row) => {
                      const isDp = row.installment_no === 0 || row.is_down_payment;
                      const rowPayment = isDp
                        ? (scheduleSheet.payments || []).find(p => p.is_down_payment)
                        : (scheduleSheet.payments || []).find(p => p.installment_no === row.installment_no);
                      const isPaid = row.status === 'paid';
                      return (
                      <TableRow key={`row-${row.installment_no}${isDp ? '-dp' : ''}`}
                        className={`border-slate-800 ${isDp ? 'bg-cyan-950/20' : ''}`}
                        data-testid={isDp ? 'schedule-row-dp' : 'schedule-row'}>
                        <TableCell className="text-white">
                          {isDp ? <span className="text-cyan-400 font-semibold text-xs">DP</span> : row.installment_no}
                        </TableCell>
                        <TableCell className="text-slate-300">
                          {isDp
                            ? <span className="text-cyan-400 text-xs font-medium">Down Payment</span>
                            : new Date(row.due_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                        </TableCell>
                        <TableCell className="text-slate-300">PKR {row.amount_due?.toLocaleString()}</TableCell>
                        <TableCell className="text-emerald-500">PKR {row.paid_amount?.toLocaleString()}</TableCell>
                        <TableCell className="text-slate-300 text-xs">
                          {row.paid_date ? new Date(row.paid_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : <span className="text-slate-600">— Not Received</span>}
                        </TableCell>
                        <TableCell className="text-amber-500">PKR {row.remaining?.toLocaleString()}</TableCell>
                        <TableCell>
                          <span className={`px-2 py-1 rounded-sm text-xs ${
                            row.status === 'paid' ? 'bg-emerald-500/20 text-emerald-500' :
                            row.status === 'overdue' ? 'bg-red-500/20 text-red-500' :
                            'bg-slate-700/50 text-slate-400'
                          }`}>{row.status}</span>
                        </TableCell>
                        <TableCell className="text-right">
                          {isPaid && rowPayment ? (
                            <Button size="sm" variant="ghost"
                              onClick={async () => {
                                try {
                                  const { data } = await axios.get(`${API_URL}/api/installments/receipt-by-payment/${rowPayment.id}?size=80mm`, { withCredentials: true });
                                  downloadOrOpenPdf(data.pdf, `Receipt_${data.receipt_number}.pdf`, 'print');
                                } catch (err) { toast.error(err.response?.data?.detail || 'Reprint failed'); }
                              }}
                              data-testid={isDp ? 'reprint-dp' : `reprint-schedule-${row.installment_no}`}
                              className="text-amber-500 hover:text-amber-400 hover:bg-slate-800 h-8 px-2" title={`Reprint receipt ${rowPayment.receipt_number}`}>
                              <Receipt className="h-4 w-4 mr-1" /> <span className="text-xs">Reprint</span>
                            </Button>
                          ) : isDp ? (
                            <Button size="sm"
                              onClick={() => openPaymentDialog(scheduleSheet.installment, { ...row, is_down_payment: true })}
                              data-testid="pay-down-payment"
                              className="bg-cyan-600 hover:bg-cyan-700 text-white h-8 px-3"
                              title="Record down payment (reduces outstanding balance)">
                              <DollarSign className="h-4 w-4 mr-1" /> <span className="text-xs">Pay DP</span>
                            </Button>
                          ) : (
                            <Button size="sm"
                              onClick={() => openPaymentDialog(scheduleSheet.installment, row)}
                              data-testid={`pay-schedule-${row.installment_no}`}
                              className="bg-emerald-600 hover:bg-emerald-700 text-white h-8 px-3"
                              title={`Record payment for installment #${row.installment_no}`}>
                              <DollarSign className="h-4 w-4 mr-1" /> <span className="text-xs">Pay</span>
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    );})}
                  </TableBody>
                </Table>

                {scheduleSheet.payments?.length > 0 && (
                  <div className="border-t border-slate-800 pt-4">
                    <h3 className="text-white font-semibold mb-2">Payment History</h3>
                    <Table>
                      <TableHeader>
                        <TableRow className="border-slate-800">
                          <TableHead className="text-slate-400">Receipt #</TableHead>
                          <TableHead className="text-slate-400">Received Date</TableHead>
                          <TableHead className="text-slate-400">Amount</TableHead>
                          <TableHead className="text-slate-400">Method</TableHead>
                          <TableHead className="text-slate-400 text-right">Reprint</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {scheduleSheet.payments.map((p) => (
                          <TableRow key={p.id} className="border-slate-800" data-testid="payment-row">
                            <TableCell className="text-slate-300 text-xs font-mono">{p.receipt_number}</TableCell>
                            <TableCell className="text-slate-300 text-xs">
                              {p.payment_date ? new Date(p.payment_date).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-'}
                            </TableCell>
                            <TableCell className="text-emerald-500">PKR {p.amount?.toLocaleString()}</TableCell>
                            <TableCell className="text-slate-300">{p.payment_method || 'Cash'}</TableCell>
                            <TableCell className="text-right">
                              <div className="flex justify-end space-x-1">
                                <Button size="sm" variant="ghost"
                                  onClick={async () => {
                                    try {
                                      const { data } = await axios.get(`${API_URL}/api/installments/receipt-by-payment/${p.id}?size=80mm`, { withCredentials: true });
                                      downloadOrOpenPdf(data.pdf, `Receipt_${data.receipt_number}.pdf`, 'print');
                                    } catch (err) { toast.error(err.response?.data?.detail || 'Reprint failed'); }
                                  }}
                                  data-testid={`reprint-receipt-${p.id}`}
                                  className="text-amber-500 hover:text-amber-400 hover:bg-slate-800 h-8 w-8 p-0" title="Reprint receipt">
                                  <Printer className="h-4 w-4" />
                                </Button>
                                <Button size="sm" variant="ghost"
                                  onClick={async () => {
                                    try {
                                      const { data } = await axios.get(`${API_URL}/api/installments/receipt-by-payment/${p.id}?size=80mm`, { withCredentials: true });
                                      downloadOrOpenPdf(data.pdf, `Receipt_${data.receipt_number}.pdf`, 'download');
                                    } catch (err) { toast.error(err.response?.data?.detail || 'Download failed'); }
                                  }}
                                  data-testid={`download-receipt-${p.id}`}
                                  className="text-emerald-500 hover:text-emerald-400 hover:bg-slate-800 h-8 w-8 p-0" title="Download receipt">
                                  <Download className="h-4 w-4" />
                                </Button>
                                {canEditDelete && (
                                  <Button size="sm" variant="ghost"
                                    onClick={async () => {
                                      const reason = window.prompt(
                                        `Revert receipt ${p.receipt_number} (PKR ${p.amount?.toLocaleString()}) back to Unpaid?\n\nThe installment's paid/remaining amount will be restored and the payment marked as reverted.\n\nOptional reason:`);
                                      if (reason === null) return; // user cancelled
                                      try {
                                        const { data } = await axios.post(
                                          `${API_URL}/api/installments/payments/${p.id}/revert`,
                                          { reason: reason || '' }, { withCredentials: true });
                                        toast.success(`Reverted to Unpaid · Remaining PKR ${data.new_remaining_amount.toLocaleString()}`);
                                        // Refresh the sheet
                                        const [scheduleRes, paymentsRes] = await Promise.all([
                                          axios.get(`${API_URL}/api/installments/schedule/${scheduleSheet.installment.id}`, { withCredentials: true }),
                                          axios.get(`${API_URL}/api/installments/payments/${scheduleSheet.installment.id}`, { withCredentials: true }),
                                        ]);
                                        setScheduleSheet(s => ({ ...s, data: scheduleRes.data, payments: paymentsRes.data || [] }));
                                        fetchAll();
                                      } catch (err) {
                                        toast.error(err.response?.data?.detail || 'Revert failed');
                                      }
                                    }}
                                    data-testid={`revert-payment-${p.id}`}
                                    className="text-red-400 hover:text-red-300 hover:bg-slate-800 h-8 w-8 p-0" title="Revert this payment to Unpaid">
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}

                <div className="flex justify-end space-x-2 pt-2 border-t border-slate-800">
                  <Button variant="outline" onClick={() => handleSchedulePdf(scheduleSheet.installment, 'print')}
                    data-testid="schedule-print-button"
                    className="border-amber-500 text-amber-500 hover:bg-amber-500 hover:text-white">
                    <Printer className="h-4 w-4 mr-2" /> Print Schedule
                  </Button>
                  <Button onClick={() => handleSchedulePdf(scheduleSheet.installment, 'download')}
                    data-testid="schedule-download-button"
                    className="bg-amber-500 hover:bg-amber-600 text-white">
                    <Download className="h-4 w-4 mr-2" /> Download PDF
                  </Button>
                </div>
              </div>
              );
            })()}
        </SheetContent>
      </Sheet>

      {/* Surety Cheque Dialog */}
      <Dialog open={suretyDialog.open} onOpenChange={(o) => setSuretyDialog({ ...suretyDialog, open: o })}>
        <DialogContent className="bg-slate-900 border-slate-800 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white">Generate Surety Cheque Consent</DialogTitle>
            <DialogDescription className="text-slate-400">
              Fill the cheque details — a signed consent PDF will be produced.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {[
              ['account_title', 'Account Title'],
              ['bank_name', 'Bank Name'],
              ['bank_branch', 'Branch'],
              ['bank_account_number', 'Bank Account #'],
              ['cheque_numbers', 'Cheque #(s) (comma-sep)'],
            ].map(([k, l]) => (
              <div key={k} className="space-y-2">
                <Label className="text-slate-300">{l}</Label>
                <Input value={suretyForm[k]} onChange={(e) => setSuretyForm({ ...suretyForm, [k]: e.target.value })}
                  data-testid={`surety-${k}`}
                  className="bg-slate-800 border-slate-700 text-white" />
              </div>
            ))}
            <div className="flex justify-end space-x-2 pt-2">
              <Button variant="ghost" onClick={() => setSuretyDialog({ open: false, installment: null })} className="text-slate-300">Cancel</Button>
              <Button onClick={handleSurety} disabled={suretySubmitting}
                data-testid="submit-surety-button"
                className="bg-purple-600 hover:bg-purple-700">
                <Download className="h-4 w-4 mr-2" />
                {suretySubmitting ? 'Generating...' : 'Generate & Download'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Box Label JPG Preview Dialog */}
      <Dialog open={labelPreview.open} onOpenChange={(o) => setLabelPreview({ ...labelPreview, open: o })}>
        <DialogContent className="bg-slate-900 border-slate-800 text-white max-w-md" data-testid="box-label-preview-dialog">
          <DialogHeader>
            <DialogTitle className="text-white">Product Box Label</DialogTitle>
            <DialogDescription className="text-slate-400">
              {labelPreview.installment?.agreement_number
                ? `Agreement ${labelPreview.installment.agreement_number}`
                : ''}
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-center justify-center bg-white rounded-sm p-4 min-h-[180px]">
            {labelPreview.loading ? (
              <div className="text-slate-500 text-sm">Rendering label...</div>
            ) : labelPreview.image ? (
              <img
                src={labelPreview.image}
                alt="Product Box Label"
                data-testid="box-label-image"
                className="max-w-full max-h-[60vh] object-contain"
              />
            ) : (
              <div className="text-slate-500 text-sm">No label to display</div>
            )}
          </div>
          <div className="flex justify-end space-x-2 pt-2">
            <Button variant="ghost" onClick={() => setLabelPreview({ open: false, installment: null, image: null, loading: false })}
              className="text-slate-300">Close</Button>
            <Button onClick={printLabelImage} disabled={!labelPreview.image}
              data-testid="print-box-label-button"
              className="bg-amber-500 hover:bg-amber-600 text-white">
              <Printer className="h-4 w-4 mr-2" /> Print Label
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
